<template>
  <el-dialog
    v-model="dialogVisible"
    title="注册新账号"
    width="440px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    append-to-body
    destroy-on-close
  >
    <el-form
      ref="registerFormRef"
      :model="registerForm"
      :rules="registerRules"
      label-position="top"
      class="register-form"
    >
      <!-- 手机号 -->
      <el-form-item label="手机号" prop="phone">
        <el-input
          v-model="registerForm.phone"
          placeholder="请输入11位手机号"
          prefix-icon="Iphone"
          clearable
          size="large"
          maxlength="11"
        />
      </el-form-item>

      <!-- 密码 -->
      <el-form-item label="密码" prop="password">
        <el-input
          v-model="registerForm.password"
          type="password"
          placeholder="至少8位，包含字母和数字"
          prefix-icon="Lock"
          show-password
          clearable
          size="large"
        />
      </el-form-item>

      <!-- 昵称（可选） -->
      <el-form-item label="昵称" prop="nickname">
        <el-input
          v-model="registerForm.nickname"
          placeholder="选填，不填则使用手机号"
          prefix-icon="User"
          clearable
          size="large"
          maxlength="20"
        />
      </el-form-item>

      <!-- 图形验证码 -->
      <el-form-item label="图形验证码" prop="captcha_code">
        <div class="captcha-row">
          <el-input
            v-model="registerForm.captcha_code"
            placeholder="请输入图形验证码"
            clearable
            size="large"
            maxlength="4"
          />
          <div class="captcha-image" @click="refreshCaptcha" :title="'点击刷新'">
            <img v-if="captchaImage" :src="captchaImage" alt="图形验证码" />
            <span v-else class="captcha-loading">加载中...</span>
          </div>
        </div>
      </el-form-item>

      <!-- 短信验证码 -->
      <el-form-item label="短信验证码" prop="sms_code">
        <div class="sms-row">
          <el-input
            v-model="registerForm.sms_code"
            placeholder="请输入6位短信验证码"
            clearable
            size="large"
            maxlength="6"
          />
          <el-button
            type="primary"
            size="large"
            :disabled="smsCountdown > 0 || smsLoading"
            :loading="smsLoading"
            @click="handleSendSms"
            class="sms-btn"
          >
            {{ smsCountdown > 0 ? `${smsCountdown}s 后重发` : '获取验证码' }}
          </el-button>
        </div>
      </el-form-item>

      <!-- 隐私协议同意 -->
      <el-form-item prop="agree" :rules="agreeRuleWrapper">
        <div class="agree-row">
          <el-checkbox v-model="registerForm.agree">
            我已阅读并同意
            <a href="javascript:void(0)" class="privacy-link" @click.prevent="showPrivacyDialog = true">
              《用户隐私协议》
            </a>
          </el-checkbox>
        </div>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        size="large"
        :loading="registerLoading"
        @click="handleRegister"
      >
        立即注册
      </el-button>
    </template>
  </el-dialog>

  <!-- 隐私协议弹窗：放在 template 根层级（与注册对话框平级），避免嵌套 dialog 的 mask/z-index 问题 -->
  <PrivacyPolicyDialog v-model="showPrivacyDialog" />
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import PrivacyPolicyDialog from './PrivacyPolicyDialog.vue'
import { getWlApi, postWlApi } from '@utils/api_v1.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'registered'])

const registerFormRef = ref()
const dialogVisible = ref(false)
const captchaImage = ref('')
const captchaId = ref('')
const smsCountdown = ref(0)
const smsLoading = ref(false)
const registerLoading = ref(false)
const showPrivacyDialog = ref(false)
let countdownTimer = null

const registerForm = reactive({
  phone: '',
  password: '',
  nickname: '',
  captcha_code: '',
  sms_code: '',
  agree: false
})

// 校验规则
const registerRules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    {
      validator: (_, value, cb) => {
        if (!/^1[3-9]\d{9}$/.test(value || '')) {
          cb(new Error('请输入正确的11位手机号'))
        } else {
          cb()
        }
      },
      trigger: 'blur'
    }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    {
      validator: (_, value, cb) => {
        if (!value || value.length < 8) {
          cb(new Error('密码至少8位'))
        } else if (!/[A-Za-z]/.test(value)) {
          cb(new Error('密码必须包含字母'))
        } else if (!/\d/.test(value)) {
          cb(new Error('密码必须包含数字'))
        } else {
          cb()
        }
      },
      trigger: 'blur'
    }
  ],
  nickname: [
    { max: 20, message: '昵称最多20个字符', trigger: 'blur' }
  ],
  sms_code: [
    { required: true, message: '请输入短信验证码', trigger: 'blur' },
    {
      validator: (_, value, cb) => {
        if (!/^\d{6}$/.test(value || '')) {
          cb(new Error('请输入6位数字短信验证码'))
        } else {
          cb()
        }
      },
      trigger: 'blur'
    }
  ]
}

// 隐私协议同意校验：当用户勾选时自动通过，未勾选时提交注册时再统一校验
const agreeRuleWrapper = computed(() => ({
  required: false
}))

watch(() => props.modelValue, (v) => {
  dialogVisible.value = v
  if (v) {
    // 每次打开对话框都重置表单并刷新图形验证码
    resetForm()
    refreshCaptcha()
  }
})

watch(dialogVisible, (v) => {
  emit('update:modelValue', v)
})

onMounted(() => {
  if (dialogVisible.value) {
    refreshCaptcha()
  }
})

const resetForm = () => {
  registerFormRef.value?.resetFields()
  registerForm.phone = ''
  registerForm.password = ''
  registerForm.nickname = ''
  registerForm.captcha_code = ''
  registerForm.sms_code = ''
  registerForm.agree = false
  stopCountdown()
}

// ============ 图形验证码 ============
const refreshCaptcha = async () => {
  try {
    const res = await getWlApi('/captcha/image')
    if (res.code === 0) {
      captchaImage.value = res.data.image
      captchaId.value = res.data.captcha_id
      registerForm.captcha_code = ''
    } else {
      ElMessage.error(res.message || '获取图形验证码失败')
    }
  } catch (e) {
    ElMessage.error('获取图形验证码失败，请检查网络')
  }
}

// ============ 短信验证码 ============
const stopCountdown = () => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
  smsCountdown.value = 0
}

const startCountdown = () => {
  smsCountdown.value = 60
  countdownTimer = setInterval(() => {
    smsCountdown.value -= 1
    if (smsCountdown.value <= 0) {
      stopCountdown()
    }
  }, 1000)
}

const handleSendSms = async () => {
  // 先校验手机号和图形验证码
  if (!/^1[3-9]\d{9}$/.test(registerForm.phone || '')) {
    ElMessage.warning('请先输入正确的手机号')
    return
  }
  if (!registerForm.captcha_code || registerForm.captcha_code.length !== 4) {
    ElMessage.warning('请先输入图形验证码')
    return
  }

  smsLoading.value = true
  try {
    const res = await postWlApi('/captcha/send_sms', {
      phone: registerForm.phone,
      captcha_id: captchaId.value,
      captcha_code: registerForm.captcha_code
    })
    if (res.code === 0) {
      ElMessage.success('验证码已发送，请注意查收')
      startCountdown()
    } else {
      ElMessage.error(res.message || '发送失败')
    }
  } catch (e) {
    ElMessage.error('发送验证码失败，请检查网络')
  } finally {
    smsLoading.value = false
  }
}

// ============ 注册 ============
const handleRegister = () => {
  registerFormRef.value.validate(async (valid) => {
    if (!valid) return

    if (!registerForm.agree) {
      ElMessage.warning('请先阅读并同意《用户隐私协议》')
      return
    }

    registerLoading.value = true
    try {
      const res = await postWlApi('/user/register', {
        phone: registerForm.phone,
        password: registerForm.password,
        sms_code: registerForm.sms_code,
        nickname: registerForm.nickname || undefined
      })

      if (res.code === 0) {
        ElMessage.success('注册成功！请使用手机号登录')
        emit('registered', res.data)
        handleClose()
      } else {
        ElMessage.error(res.message || '注册失败')
      }
    } catch (e) {
      ElMessage.error('注册请求失败，请稍后重试')
    } finally {
      registerLoading.value = false
    }
  })
}

const handleClose = () => {
  stopCountdown()
  dialogVisible.value = false
}
</script>

<style scoped>
.register-form {
  padding-top: 8px;
}

.captcha-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.captcha-row .el-input {
  flex: 1;
}

.captcha-image {
  width: 120px;
  height: 40px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.2s;
}

.captcha-image:hover {
  border-color: #409eff;
}

.captcha-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.captcha-loading {
  font-size: 12px;
  color: #909399;
}

.sms-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.sms-row .el-input {
  flex: 1;
}

.sms-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.agree-row {
  width: 100%;
}

.privacy-link {
  color: #409eff;
  text-decoration: none;
}

.privacy-link:hover {
  text-decoration: underline;
}
</style>
